import React, { useState } from 'react';
import { useDesignerModeStore } from '@flow-studio/store';
import type { FlowObserverTabContentProps } from '@flow-studio/flow-observer-core';
const FolderIcon = ({ size = 20 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
  </svg>
);
const CopyIcon = ({ size = 20 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
  </svg>
);
const CheckIcon = ({ size = 20 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// ── Port colors ───────────────────────────────────────────────────────────────

const PORT_COLORS: Record<string, string> = {
  success: '#22c55e',
  approved: '#22c55e',
  completed: '#22c55e',
  error: '#ef4444',
  failed: '#ef4444',
  rejected: '#ef4444',
  waiting: '#f59e0b',
  pending: '#f59e0b',
};

function getPortColor(port?: string): string {
  if (!port) return '#9ca3af';
  return PORT_COLORS[port.toLowerCase()] ?? '#9ca3af';
}

// ── JSON Data Viewer (Tree / JSON / Stats) ────────────────────────────────────

type ViewMode = 'tree' | 'json';

const TYPE_COLORS: Record<string, string> = {
  string: '#22c55e',
  number: '#3b82f6',
  boolean: '#f59e0b',
  null: '#ef4444',
  array: '#8b5cf6',
  object: '#06b6d4',
};

function getValueType(val: unknown): string {
  if (val === null) return 'null';
  if (Array.isArray(val)) return 'array';
  return typeof val;
}


function TreeNode({ nodeKey, value, depth = 0 }: { nodeKey?: string; value: unknown; depth?: number }): React.ReactElement {
  const type = getValueType(value);
  const isExpandable = type === 'object' || type === 'array';
  const [open, setOpen] = useState(depth < 2);
  const entries = isExpandable ? Object.entries(value as Record<string, unknown>) : [];

  return (
    <div style={{ paddingLeft: depth > 0 ? 12 : 0 }}>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 4, minHeight: 18, cursor: isExpandable ? 'pointer' : 'default' }}
        onClick={isExpandable ? () => setOpen((v) => !v) : undefined}
      >
        <span style={{
          width: 10, fontSize: 8, color: '#6b7280', flexShrink: 0,
          display: 'inline-block',
          transform: isExpandable && open ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform 0.12s',
        }}>
          {isExpandable ? '▶' : ''}
        </span>
        {nodeKey !== undefined && (
          <span style={{ color: '#cbd5e1', fontSize: 11, flexShrink: 0 }}>{nodeKey}:</span>
        )}
        <span style={{ color: TYPE_COLORS[type] ?? '#d1d5db', fontSize: 11 }}>
          {isExpandable
            ? type === 'array' ? `Array[${entries.length}]` : `Object{${entries.length}}`
            : type === 'null' ? 'null'
            : type === 'string' ? `"${String(value).substring(0, 60)}${String(value).length > 60 ? '…' : ''}"`
            : String(value)
          }
        </span>
        <span style={{ fontSize: 9, color: '#6b7280', marginLeft: 2, flexShrink: 0 }}>{type}</span>
      </div>
      {isExpandable && open && entries.map(([k, v]) => (
        <TreeNode key={k} nodeKey={k} value={v} depth={depth + 1} />
      ))}
    </div>
  );
}

const TAB_ICONS: Record<ViewMode, React.ReactNode> = {
  tree: <><FolderIcon size={12} /> Tree View</>,
  json: <>{'{ }'} JSON</>,
};

type ViewFormat = 'formatted' | 'json';
type DetailLevel = 'information' | 'advanced';

const JsonDataViewer: React.FC<{ data: Record<string, unknown> }> = ({ data }) => {
  const [format, setFormat] = useState<ViewFormat>('formatted');
  const [detail, setDetail] = useState<DetailLevel>('information');
  const [view, setView] = useState<ViewMode>('tree');
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2)).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 4, overflow: 'hidden', marginTop: 4 }}>
      {/* Main format tabs */}
      <div style={{ display: 'flex', alignItems: 'center', background: '#1a2332', padding: '3px 4px', gap: 2, borderBottom: '1px solid #0f172a' }}>
        {(['formatted', 'json'] as ViewFormat[]).map((fmt) => (
          <button
            key={fmt}
            onClick={() => {
              setFormat(fmt);
              if (fmt === 'formatted') setDetail('information');
            }}
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              padding: '3px 10px', border: 'none', borderRadius: 3,
              background: format === fmt ? '#3A8C45' : 'transparent',
              color: format === fmt ? '#fff' : '#9ca3af',
              fontSize: 11, fontWeight: format === fmt ? 600 : 400, cursor: 'pointer',
              textTransform: 'capitalize',
            }}
          >
            {fmt === 'formatted' ? 'Formatted' : '{ } JSON'}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <button
          onClick={handleCopy}
          style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 10px', border: 'none', borderRadius: 3, background: 'transparent', color: copied ? '#4ade80' : '#9ca3af', fontSize: 11, cursor: 'pointer' }}
        >
          {copied ? <><CheckIcon size={12} /> Copied</> : <><CopyIcon size={12} /> Copy</>}
        </button>
      </div>

      {/* Detail level tabs (only shown in formatted mode) */}
      {format === 'formatted' && (
        <div style={{ display: 'flex', alignItems: 'center', background: '#0f172a', padding: '3px 4px', gap: 2 }}>
          {(['information', 'advanced'] as DetailLevel[]).map((level) => (
            <button
              key={level}
              onClick={() => setDetail(level)}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '3px 10px', border: 'none', borderRadius: 3,
                background: detail === level ? '#2563eb' : 'transparent',
                color: detail === level ? '#fff' : '#9ca3af',
                fontSize: 11, fontWeight: detail === level ? 600 : 400, cursor: 'pointer',
                textTransform: 'capitalize',
              }}
            >
              {level}
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      <div style={{ background: '#1e293b', padding: '8px 10px', maxHeight: 240, overflowY: 'auto', fontFamily: 'monospace' }}>
        {format === 'formatted' && detail === 'information' && (
          <div>
            {Object.entries(data).map(([k, v]) => (
              <div key={k} style={{ marginBottom: '8px', padding: '4px', background: '#0f172a', borderRadius: 2 }}>
                <div style={{ color: '#cbd5e1', fontSize: 10, fontWeight: 600, marginBottom: 2 }}>{k}</div>
                <div style={{ color: '#9ca3af', fontSize: 11 }}>{String(v).substring(0, 100)}{String(v).length > 100 ? '...' : ''}</div>
              </div>
            ))}
          </div>
        )}
        {format === 'formatted' && detail === 'advanced' && (
          <div>
            <div style={{ marginBottom: '12px', padding: '4px', borderBottom: '1px solid #334155' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: '#cbd5e1', marginBottom: '6px' }}>Tree View</div>
              {Object.entries(data).map(([k, v]) => <TreeNode key={k} nodeKey={k} value={v} depth={0} />)}
            </div>
          </div>
        )}
        {format === 'json' && (
          <pre style={{ margin: 0, fontSize: 11, color: '#9ca3af', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {JSON.stringify(data, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
};

// ── Collapsible Section ───────────────────────────────────────────────────────

interface CollapsibleSectionProps {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({ title, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderBottom: '1px solid #f3f4f6' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          width: '100%', padding: '8px 12px',
          background: '#f9fafb', border: 'none', cursor: 'pointer',
          fontSize: '12px', fontWeight: 600, color: '#374151', textAlign: 'left',
        }}
      >
        <span style={{ fontSize: '10px', transform: open ? 'rotate(90deg)' : 'rotate(0deg)', display: 'inline-block', transition: 'transform 0.15s' }}>▶</span>
        {title}
      </button>
      {open && (
        <div style={{ padding: '8px 12px' }}>
          {children}
        </div>
      )}
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

export const NodeInspectorTabContent: React.FC<FlowObserverTabContentProps> = ({
  loadState,
  error,
  onRetry,
}) => {
  const selectedNodeKey = useDesignerModeStore((s) => s.selectedInspectorNodeKey);
  const nodeInspectorData = useDesignerModeStore((s) => s.nodeInspectorData);

  if (loadState === 'loading') {
    return <div className="fop-loading">Loading…</div>;
  }

  if (loadState === 'error' && error) {
    return (
      <div className="fop-error" role="alert">
        <span>{error.message}</span>
        {error.retryable && (
          <button className="fop-error__retry" onClick={onRetry}>Retry</button>
        )}
      </div>
    );
  }

  if (!selectedNodeKey) {
    return (
      <div className="fop-empty">
        <span>Select a node from the Nodes tab to inspect it.</span>
      </div>
    );
  }

  const data = nodeInspectorData.get(selectedNodeKey);

  if (!data) {
    return (
      <div className="fop-empty">
        <span>No inspector data available for <strong>{selectedNodeKey}</strong>.</span>
      </div>
    );
  }

  const portColor = getPortColor(data.outputPortKey);

  return (
    <div style={{ overflowY: 'auto', height: '100%', fontSize: '13px' }}>
      {/* Node header */}
      <div style={{ padding: '8px 12px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ fontWeight: 600, color: '#111827', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selectedNodeKey}
        </span>
        {data.outputPortKey && (
          <span style={{ padding: '2px 8px', borderRadius: '9999px', background: portColor, color: '#fff', fontSize: '11px', fontWeight: 700, textTransform: 'capitalize' }}>
            {data.outputPortKey}
          </span>
        )}
      </div>

      {/* Input section */}
      <CollapsibleSection title="Input">
        <JsonDataViewer data={(data.input ?? {}) as Record<string, unknown>} />
      </CollapsibleSection>

      {/* Output section */}
      <CollapsibleSection title="Output">
        <JsonDataViewer data={(data.output ?? {}) as Record<string, unknown>} />
      </CollapsibleSection>

      {/* Metadata section */}
      <CollapsibleSection title="Metadata">
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <tbody>
            {data.status && (
              <tr>
                <td style={{ padding: '3px 0', color: '#6b7280', width: '40%' }}>Status</td>
                <td style={{ padding: '3px 0', color: '#111827', fontWeight: 500 }}>{data.status}</td>
              </tr>
            )}
            {data.durationMs !== undefined && (
              <tr>
                <td style={{ padding: '3px 0', color: '#6b7280' }}>Duration</td>
                <td style={{ padding: '3px 0', color: '#111827' }}>{data.durationMs}ms</td>
              </tr>
            )}
            {data.retryAttempt !== undefined && (
              <tr>
                <td style={{ padding: '3px 0', color: '#6b7280' }}>Retry</td>
                <td style={{ padding: '3px 0', color: '#111827' }}>{data.retryAttempt} / {data.maxRetries ?? '?'}</td>
              </tr>
            )}
            {data.startedAt && (
              <tr>
                <td style={{ padding: '3px 0', color: '#6b7280' }}>Started</td>
                <td style={{ padding: '3px 0', color: '#111827' }}>{new Date(data.startedAt).toLocaleTimeString()}</td>
              </tr>
            )}
            {data.completedAt && (
              <tr>
                <td style={{ padding: '3px 0', color: '#6b7280' }}>Completed</td>
                <td style={{ padding: '3px 0', color: '#111827' }}>{new Date(data.completedAt).toLocaleTimeString()}</td>
              </tr>
            )}
          </tbody>
        </table>
      </CollapsibleSection>
    </div>
  );
};
